import { CSSProperties, KeyboardEvent, MouseEvent, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { MdArrowOutward, MdClose, MdMarkEmailRead, MdNotificationsNone } from 'react-icons/md';

import { categoryTokenFromTag } from '@utils/notificationCategory';
import SnoozeButton from '@molecules/SnoozeButton/SnoozeButton';
import { hasBoundDocumentTarget } from '@utils/notificationTarget';
import { RouteNames } from '@/router/router.types';
import { JOB_ACTION_ICONS } from '@services/backgroundJobs/jobActionIcons';

import { useNow } from '@srm/ui/hooks/useNow';
import { formatRelativeTime } from './relativeTime';
import { NotificationToastProps } from './NotificationToast.types';

import './NotificationToast.scss';

/** Период тика относительного времени, мс. */
const TICK_MS = 10_000;
const FALLBACK_COLOR = 'var(--neutral-60)';

/**
 * Стопка всплывающих превью новых уведомлений у колокольчика (вариант «Карточки», стиль A).
 * Новейшее — сверху и на переднем плане; старые — мягким списком (scale/opacity); сверх
 * `maxVisible` сворачивается в «+N в колокольчике». Время поступления тикает на лету.
 *
 * Чисто презентационный: очередь, авто-скрытие и действия держит родитель ({@link Navbar}).
 */
const NotificationToast = ({
    items,
    maxVisible = 3,
    onOpen,
    onMarkRead,
    onSnoozePreset,
    onSnoozeCustom,
    onDismiss,
    onHoverChange,
    onShowAll,
    onJobAction,
    downloadingJobIds,
}: NotificationToastProps) => {
    const [hovered, setHovered] = useState(false);
    const [menuItemId, setMenuItemId] = useState<string | null>(null);

    // Относительное время идёт, пока есть что показывать.
    const now = useNow(items.length > 0 ? TICK_MS : null);

    // Пауза авто-скрытия, пока курсор над стопкой ИЛИ открыто меню «Отложить».
    useEffect(() => {
        onHoverChange?.(hovered || menuItemId !== null);
    }, [hovered, menuItemId, onHoverChange]);

    // Стопка опустела (авто-скрытие/закрытие) — снять «залипшую» паузу: при пустом списке
    // карточек нет и onMouseLeave уже не придёт.
    useEffect(() => {
        if (items.length === 0) {
            setHovered(false);
            setMenuItemId(null);
        }
    }, [items.length]);

    // Уже показанные id — чтобы анимировать появление только новых карточек.
    const seen = useRef<Set<string>>(new Set());
    useEffect(() => {
        seen.current = new Set(items.map((n) => n.id));
    });

    if (items.length === 0) return null;

    const visible = items.slice(0, maxVisible);
    const hidden = items.length - visible.length;

    // Карточка — ссылка; экшн-кнопки гасят и навигацию (preventDefault), и всплытие.
    const stop = (fn: () => void) => (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        fn();
    };

    return (
        <div
            className="ntoast"
            role="region"
            aria-live="polite"
            aria-label="Новые уведомления"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            {visible.map((n, i) => {
                const token = categoryTokenFromTag(n.tag);
                const Icon = token?.icon ?? MdNotificationsNone;
                const color = token?.color ?? FALLBACK_COLOR;
                const isFresh = i === 0 && !seen.current.has(n.id);
                const { job } = n;
                const ActionIcon = job?.action ? JOB_ACTION_ICONS[job.action.type] : null;
                const actionBusy = job?.action?.type === 'download' && Boolean(downloadingJobIds?.has(job.jobId));
                // Превью итога задачи — без страницы уведомления: ссылка ведёт в документ задачи, если он есть.
                const href = job
                    ? job.docId
                        ? RouteNames.DOCUMENT.replace(':id', job.docId)
                        : undefined
                    : `/notification/${n.id}`;
                const cardClassName = clsx(
                    'ntoast__card',
                    job && 'ntoast__card--job',
                    i === 1 && 'ntoast__card--i1',
                    i >= 2 && 'ntoast__card--i2',
                    isFresh && 'ntoast__card--enter',
                    menuItemId === n.id && 'ntoast__card--menu',
                );
                const cardStyle = { '--ntc': color, zIndex: 10 - i } as CSSProperties;
                const content = (
                    <>
                        <span
                            className="ntoast__icon"
                            style={{ color, background: `color-mix(in srgb, ${color} 6%, var(--neutral-00))` }}
                        >
                            <Icon size={19} />
                        </span>

                        <div className="ntoast__body">
                            {token && (
                                <div className="ntoast__cat" style={{ color }}>
                                    {token.label}
                                </div>
                            )}
                            <div className="ntoast__subject">{n.subject}</div>
                            {job?.text && (
                                <div className={clsx('ntoast__text', job.failed && 'ntoast__text--failed')}>
                                    {job.text}
                                </div>
                            )}

                            {job ? (
                                job.action && (
                                    <div className="ntoast__foot">
                                        <button
                                            type="button"
                                            className="ntoast__action"
                                            aria-busy={actionBusy || undefined}
                                            aria-disabled={actionBusy || undefined}
                                            onClick={stop(() => !actionBusy && onJobAction?.(n))}
                                        >
                                            {ActionIcon && <ActionIcon size={15} />}
                                            {job.action.label}
                                        </button>
                                    </div>
                                )
                            ) : (
                                <div className="ntoast__foot">
                                    <span className="ntoast__open">
                                        <MdArrowOutward size={15} />
                                        Открыть
                                    </span>
                                    <span className="ntoast__sp" />
                                    <button
                                        type="button"
                                        className="ntoast__btn"
                                        title="Прочитать"
                                        aria-label="Прочитать"
                                        onClick={stop(() => onMarkRead(n))}
                                    >
                                        <MdMarkEmailRead size={16} />
                                    </button>
                                    {hasBoundDocumentTarget(n) && (
                                        <SnoozeButton
                                            buttonClassName="ntoast__btn"
                                            align="right"
                                            onPickPreset={(date) => onSnoozePreset(n, date)}
                                            onPickCustom={() => onSnoozeCustom(n)}
                                            onOpenChange={(open) => setMenuItemId(open ? n.id : null)}
                                        />
                                    )}
                                </div>
                            )}
                        </div>

                        <span className="ntoast__time">{formatRelativeTime(n.receivedAt, now)}</span>
                        <button
                            type="button"
                            className="ntoast__close"
                            title="Скрыть превью"
                            aria-label="Скрыть превью"
                            onClick={stop(() => onDismiss(n.id))}
                        >
                            <MdClose size={13} />
                        </button>
                    </>
                );

                // Без ссылки открывает колокольчик на строке задачи кнопка во всю карточку под её содержимым: действия —
                // её соседи, а не потомки (у role=button потомки для скринридера немые).
                if (href === undefined) {
                    return (
                        <div key={n.id} className={cardClassName} style={cardStyle}>
                            <button
                                type="button"
                                className="ntoast__hit"
                                aria-label={[n.subject, job?.text].filter(Boolean).join('. ')}
                                onClick={() => onOpen(n)}
                            />
                            {content}
                        </div>
                    );
                }

                return (
                    <a
                        key={n.id}
                        href={href}
                        tabIndex={0}
                        className={cardClassName}
                        style={cardStyle}
                        onClick={(e: MouseEvent) => {
                            // плейн-клик → предпросмотр в дропдауне; средняя/ctrl/cmd/shift — ссылка (новая вкладка)
                            if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                            e.preventDefault();
                            onOpen(n);
                        }}
                        onKeyDown={(e: KeyboardEvent) => {
                            if (e.target !== e.currentTarget || e.key !== 'Enter') return;
                            e.preventDefault();
                            onOpen(n);
                        }}
                    >
                        {content}
                    </a>
                );
            })}

            {hidden > 0 && (
                <button type="button" className="ntoast__more" onClick={onShowAll}>
                    + ещё {hidden} в колокольчике
                </button>
            )}
        </div>
    );
};

export default NotificationToast;
